import { useState } from 'react';
import useFetchAdminIam from '../../api/useFetchAdminIam';
import useUpdateAdminIam from '../../api/useUpdateAdminIam';
import useResetAdminPassword from '../../api/useResetAdminPassword';
import AdminFormInput from '../../components/atoms/AdminFormInput';
import { AdminButton } from '../../components/AdminButton';
import AdminLoadingState from '../../components/molecules/AdminLoadingState';
import AdminErrorState from '../../components/molecules/AdminErrorState';

export default function AdminSettingsPage() {
  const { data: currentUser, isLoading, isError } = useFetchAdminIam();
  const { mutateAsync: updateIam } = useUpdateAdminIam();
  const { mutateAsync: resetPassword } = useResetAdminPassword();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // 現在のユーザー情報をフォームに反映
  if (currentUser && email === '') {
    setEmail(currentUser.email);
  }

  if (isLoading) return <AdminLoadingState />;
  if (isError || !currentUser)
    return <AdminErrorState message="管理者情報の読み込みに失敗しました。" />;

  const handleUpdateEmail = async () => {
    if (!email || email === currentUser.email) {
      setErrorMessage('メールアドレスを変更してください。');
      return;
    }

    setIsSaving(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      await updateIam({ email });
      setSuccessMessage('メールアドレスを更新しました。');
    } catch (error) {
      setErrorMessage('メールアドレスの更新に失敗しました。');
    } finally {
      setIsSaving(false);
    }
  };

  const handleResetPassword = async () => {
    if (!password || !passwordConfirmation) {
      setErrorMessage('パスワードを入力してください。');
      return;
    }

    if (password !== passwordConfirmation) {
      setErrorMessage('パスワードが一致しません。');
      return;
    }

    if (password.length < 8) {
      setErrorMessage('パスワードは8文字以上で入力してください。');
      return;
    }

    setIsChangingPassword(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      await resetPassword({ password, password_confirmation: passwordConfirmation });
      setSuccessMessage('パスワードを変更しました。');
      setPassword('');
      setPasswordConfirmation('');
    } catch (error) {
      setErrorMessage('パスワードの変更に失敗しました。');
    } finally {
      setIsChangingPassword(false);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <h1 className="my-0 text-2xl leading-[1.2] font-bold text-[#333]">設定</h1>

      {/* 成功・エラーメッセージ */}
      {successMessage && (
        <div className="rounded bg-[#e8f5e9] px-4 py-3 text-sm text-[#2e7d32]">
          {successMessage}
        </div>
      )}
      {errorMessage && (
        <div className="rounded bg-[#ffebee] px-4 py-3 text-sm text-[#c62828]">{errorMessage}</div>
      )}

      {/* 管理者情報 */}
      <div className="rounded border border-solid border-[#ccc] bg-white p-6 shadow-[0px_0px_6px_0px_rgba(0,0,0,0.2)]">
        <h2 className="mb-4 text-lg font-bold text-[#333]">管理者情報</h2>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <label className="text-sm font-bold text-[#333]">氏名</label>
            <div className="text-sm text-[#666]">{currentUser.name}</div>
          </div>
          <div className="flex flex-col gap-2">
            <label className="text-sm font-bold text-[#333]">ロール</label>
            <div className="text-sm text-[#666]">
              {currentUser.role === 'super_admin' && 'スーパー管理者'}
              {currentUser.role === 'admin' && '管理者'}
              {currentUser.role === 'marker_manager' && 'マーカーマネージャー'}
            </div>
          </div>
        </div>
      </div>

      {/* メールアドレス変更 */}
      <div className="rounded border border-solid border-[#ccc] bg-white p-6 shadow-[0px_0px_6px_0px_rgba(0,0,0,0.2)]">
        <h2 className="mb-4 text-lg font-bold text-[#333]">メールアドレスの変更</h2>
        <div className="flex flex-col gap-4">
          <AdminFormInput
            label="新しいメールアドレス"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="example@example.com"
            type="email"
          />
          <div className="flex justify-end">
            <AdminButton
              label={isSaving ? '保存中...' : '保存'}
              onClick={handleUpdateEmail}
              disabled={isSaving}
            />
          </div>
        </div>
      </div>

      {/* パスワード変更 */}
      <div className="rounded border border-solid border-[#ccc] bg-white p-6 shadow-[0px_0px_6px_0px_rgba(0,0,0,0.2)]">
        <h2 className="mb-4 text-lg font-bold text-[#333]">パスワードの変更</h2>
        <div className="flex flex-col gap-4">
          <AdminFormInput
            label="新しいパスワード"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="8文字以上で入力してください"
            type="password"
          />
          <AdminFormInput
            label="パスワード（確認用）"
            value={passwordConfirmation}
            onChange={(e) => setPasswordConfirmation(e.target.value)}
            placeholder="もう一度入力してください"
            type="password"
          />
          <div className="flex justify-end">
            <AdminButton
              label={isChangingPassword ? '変更中...' : 'パスワードを変更'}
              onClick={handleResetPassword}
              disabled={isChangingPassword}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
